import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useStaffStatus } from '../customHook/getrole';
import { refreshSessionFlags } from '../utils/sessionFlags';

/**
 * Show the page to staff accounts and send everyone else to the script list.
 *
 * The check runs on every render rather than once when the routes are built.
 * The route table used to call GetRole() at import, which froze the answer at
 * whatever the browser held when the app first loaded, before any sign in.
 *
 * A session whose flag is not known yet waits for one user info call before
 * deciding, so opening an edit link straight after this ships does not bounce
 * a staff account. If that call fails the page is treated as not allowed.
 * This is only about what the browser shows, the server refuses non staff
 * changes on its own.
 */
const StaffOnly = ({ children }: { children: React.ReactElement }) => {
  const status = useStaffStatus();
  const [lookupFailed, setLookupFailed] = useState(false);

  useEffect(() => {
    if (status !== 'unknown') {
      return undefined;
    }
    let live = true;
    refreshSessionFlags().then((stored) => {
      if (live && !stored) {
        setLookupFailed(true);
      }
    });
    return () => {
      live = false;
    };
  }, [status]);

  if (status === 'staff') {
    return children;
  }
  if (status === 'unknown' && !lookupFailed) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Checking access...</span>
        </div>
      </div>
    );
  }
  return <Navigate to="/app/allscripts" replace />;
};

export default StaffOnly;
